/**
 * @Module: Price Context
 * @Status: Ready
 * @Summary: React context holding the ingredient price registry with USDA auto-seeding
 * @Description: PriceProvider wraps the app and exposes usePrices() which returns
 *   { prices, loading, updatePrice, deletePrice, getPriceFor }. Prices are
 *   persisted in localStorage as primary storage. When the user is authenticated
 *   and Firestore is available, prices sync to /users/{uid}/ingredientPrices.
 *   The Map is keyed by normalized ingredient name for O(1) lookup.
 *   On first load (no existing prices), auto-seeds from USDA price data with
 *   source='api'. Manual prices (source='manual') always override USDA defaults.
 * @NextSteps: Add real-time Firestore listener via onSnapshot
 */

import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from './AuthContext';
import { isFirebaseConfigured, db } from '../config/firebase';
import { createPriceService } from '../services/firestore/price.service';
import type { IngredientPrice, PriceSource } from '../types/ingredient-price';
import { PRICE_SCHEMA_VERSION } from '../types/ingredient-price';
import { AppError } from '../services/firestore/errors';
import { util_log_debug_01 } from '../utils/logging';
import usdaPrices from '../data/usda-prices.json';
import { categorize } from '../utils/ingredientCategories';

const STORAGE_KEY = 'choppy:prices';
const SEEDED_KEY = 'choppy:prices:usda-seeded';

// --- USDA seed types ---

interface UsdaPriceEntry {
  name: string;
  displayName: string;
  pricePerLb: number;
  costPerGram: number;
  category: string;
  source: string;
  form: string;
}

/** Convert a USDA price entry into an IngredientPrice with source='api'. */
function usdaToIngredientPrice(entry: UsdaPriceEntry): IngredientPrice {
  const now = new Date().toISOString();
  return {
    id: `usda-${entry.name.replace(/\s+/g, '-')}`,
    name: entry.name,
    displayName: entry.displayName,
    price: +entry.pricePerLb.toFixed(2),
    quantity: 1,
    unit: 'lb',
    costPerBaseUnit: entry.costPerGram,
    baseUnit: 'g',
    category: categorize(entry.name) || entry.category,
    source: 'api' as PriceSource,
    recordedAt: now,
    updatedAt: now,
    schemaVersion: PRICE_SCHEMA_VERSION,
  };
}

/** Build the USDA seed map (only called once on first load). */
function buildUsdaSeedMap(): Map<string, IngredientPrice> {
  const map = new Map<string, IngredientPrice>();
  for (const entry of usdaPrices as UsdaPriceEntry[]) {
    map.set(entry.name, usdaToIngredientPrice(entry));
  }
  return map;
}

// --- localStorage helpers ---

function loadLocalPrices(): IngredientPrice[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as IngredientPrice[]) : [];
  } catch {
    return [];
  }
}

function saveLocalPrices(prices: IngredientPrice[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prices));
  } catch { /* quota exceeded */ }
}

function arrayToMap(arr: IngredientPrice[]): Map<string, IngredientPrice> {
  const map = new Map<string, IngredientPrice>();
  for (const p of arr) map.set(p.name, p);
  return map;
}

/** Naive singularize: strip trailing s/es for fuzzy matching. */
function singularize(word: string): string {
  if (word.endsWith('ies') && word.length > 4) return word.slice(0, -3) + 'y';
  if (word.endsWith('oes') && word.length > 4) return word.slice(0, -2);
  if (word.endsWith('ves') && word.length > 4) return word.slice(0, -3) + 'f';
  if (word.endsWith('ses') || word.endsWith('shes') || word.endsWith('ches') || word.endsWith('xes') || word.endsWith('zes')) {
    return word.slice(0, -2);
  }
  if (word.endsWith('s') && !word.endsWith('ss') && !word.endsWith('us') && word.length > 3) {
    return word.slice(0, -1);
  }
  return word;
}

// --- Context ---

interface PriceContextValue {
  prices: Map<string, IngredientPrice>;
  loading: boolean;
  updatePrice: (price: IngredientPrice) => Promise<void>;
  deletePrice: (id: string) => Promise<void>;
  getPriceFor: (ingredientName: string) => IngredientPrice | undefined;
}

const PriceContext = createContext<PriceContextValue | undefined>(undefined);

export const PriceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [prices, setPrices] = useState<Map<string, IngredientPrice>>(new Map());
  const [loading, setLoading] = useState(true);
  const firestoreDown = useRef(false);

  const serviceRef = useRef(
    isFirebaseConfigured() && db ? createPriceService(db) : null,
  );

  const uid = user?.uid ?? null;

  const isServiceUnavailable = (err: unknown): boolean =>
    err instanceof AppError && err.code === 'SERVICE_UNAVAILABLE';

  const notifyLocalFallback = useCallback(() => {
    if (!firestoreDown.current) {
      firestoreDown.current = true;
      toast({
        title: 'Prices saved locally',
        description: 'Enable Firestore for cloud sync.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
    }
  }, [toast]);

  // Load prices on mount and when user changes.
  // If no prices exist yet, auto-seed from USDA data (source='api').
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let loaded: IngredientPrice[] = [];

      if (!uid || firestoreDown.current || !serviceRef.current) {
        loaded = loadLocalPrices();
        util_log_debug_01('PriceContext: loaded from localStorage');
      } else {
        try {
          loaded = await serviceRef.current.list(uid);
          util_log_debug_01(`PriceContext: loaded ${loaded.length} prices from Firestore`);
        } catch (err) {
          if (isServiceUnavailable(err)) {
            notifyLocalFallback();
            loaded = loadLocalPrices();
          }
        }
      }

      // Auto-seed from USDA if no prices exist (always seed when empty,
      // regardless of the seeded flag — covers cases where flag was set
      // but prices didn't persist, or localStorage was cleared)
      if (loaded.length === 0) {
        const seedMap = buildUsdaSeedMap();
        setPrices(seedMap);
        saveLocalPrices(Array.from(seedMap.values())); // persist immediately
        localStorage.setItem(SEEDED_KEY, '1');
        util_log_debug_01(`PriceContext: auto-seeded ${seedMap.size} USDA prices`);
      } else {
        // Merge USDA prices for any NEW items not yet in the user's price book
        const existingMap = arrayToMap(loaded);
        const seedMap = buildUsdaSeedMap();
        let added = 0;
        seedMap.forEach((usdaPrice, name) => {
          if (!existingMap.has(name)) {
            existingMap.set(name, usdaPrice);
            added++;
          }
        });
        setPrices(existingMap);
        if (added > 0) {
          util_log_debug_01(`PriceContext: merged ${added} new USDA prices`);
        }
      }

      setLoading(false);
    };

    load();
  }, [uid, notifyLocalFallback]);

  // Persist to localStorage whenever prices change
  useEffect(() => {
    saveLocalPrices(Array.from(prices.values()));
  }, [prices]);

  const updatePrice = useCallback(async (price: IngredientPrice) => {
    // Optimistic local update
    setPrices((prev) => {
      const next = new Map(prev);
      next.set(price.name, price);
      return next;
    });

    if (!uid || firestoreDown.current || !serviceRef.current) {
      notifyLocalFallback();
      return;
    }

    try {
      const saved = await serviceRef.current.save(uid, price);
      setPrices((prev) => {
        const next = new Map(prev);
        next.set(saved.name, saved);
        return next;
      });
    } catch (err) {
      if (isServiceUnavailable(err)) {
        notifyLocalFallback();
      }
    }
  }, [uid, notifyLocalFallback]);

  const deletePrice = useCallback(async (id: string) => {
    // Find the price by id to remove from map
    setPrices((prev) => {
      const next = new Map(prev);
      const entries = Array.from(next.entries());
      for (let i = 0; i < entries.length; i++) {
        if (entries[i][1].id === id) { next.delete(entries[i][0]); break; }
      }
      return next;
    });

    if (!uid || firestoreDown.current || !serviceRef.current) return;

    try {
      await serviceRef.current.delete(uid, id);
    } catch (err) {
      if (isServiceUnavailable(err)) {
        notifyLocalFallback();
      }
    }
  }, [uid, notifyLocalFallback]);

  const getPriceFor = useCallback((ingredientName: string): IngredientPrice | undefined => {
    const key = ingredientName.toLowerCase().trim();

    // 1. Exact match
    const exact = prices.get(key);
    if (exact) return exact;

    // 2. Singularize (tomatoes → tomato)
    const singular = singularize(key);
    if (singular !== key) {
      const m = prices.get(singular);
      if (m) return m;
    }

    // 3. Pluralize (tomato → tomatos)
    const plural = key + 's';
    const pm = prices.get(plural);
    if (pm) return pm;

    // 4. Strip common adjectives/modifiers and try base ingredient
    const STRIP_WORDS = [
      'fresh', 'dried', 'ground', 'diced', 'minced', 'chopped', 'sliced',
      'grated', 'shredded', 'crushed', 'frozen', 'canned', 'cooked',
      'raw', 'organic', 'large', 'small', 'medium', 'whole', 'ripe',
      'unsalted', 'salted', 'roasted', 'toasted', 'smoked', 'boneless',
      'skinless', 'extra-virgin', 'extra', 'firm', 'soft', 'plain',
      'all-purpose', 'kosher', 'sea', 'light', 'dark', 'sweet',
      'hot', 'cold', 'warm', 'red', 'green', 'white', 'yellow', 'black',
    ];
    const words = key.split(/\s+/);
    const stripped = words.filter(w => !STRIP_WORDS.includes(w)).join(' ');
    if (stripped && stripped !== key) {
      const sm = prices.get(stripped) || prices.get(singularize(stripped));
      if (sm) return sm;
    }

    // 5. Try just the last word (often the base: "basmati rice" → "rice")
    if (words.length > 1) {
      const lastWord = words[words.length - 1];
      const lm = prices.get(lastWord) || prices.get(singularize(lastWord));
      if (lm) return lm;
    }

    // 6. Try just the last two words ("olive oil" from "extra-virgin olive oil")
    if (words.length > 2) {
      const lastTwo = words.slice(-2).join(' ');
      const l2m = prices.get(lastTwo) || prices.get(singularize(lastTwo));
      if (l2m) return l2m;
    }

    return undefined;
  }, [prices]);

  return (
    <PriceContext.Provider value={{ prices, loading, updatePrice, deletePrice, getPriceFor }}>
      {children}
    </PriceContext.Provider>
  );
};

export function usePrices(): PriceContextValue {
  const ctx = useContext(PriceContext);
  if (!ctx) throw new Error('usePrices must be used inside <PriceProvider>');
  return ctx;
}
